import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Hand, Sparkles, History, Heart, Book, MessageCircle, HelpCircle, LogIn, LogOut, User, Star, Bell, AlertTriangle, Info, Shield, Brain, Check, X, ChevronRight, Trophy, RotateCcw } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import PlaylistToggle from '@/components/PlaylistToggle';
import QuizDiscoverChatSection from '@/components/QuizDiscoverChatSection';

// Mini Quiz Questions (5 questions for landing page)
const MINI_QUIZ = [
  {
    id: 1,
    question: "Quelle ligne représente la vitalité et l'énergie ?",
    options: ["Ligne de cœur", "Ligne de vie", "Ligne de tête", "Ligne du destin"],
    correctAnswer: 1,
    explanation: "La ligne de vie représente l'énergie vitale et la santé, pas la durée de vie !"
  },
  {
    id: 2,
    question: "Quelle main représente le potentiel inné ?",
    options: ["Main droite", "Main gauche", "Les deux", "Aucune"],
    correctAnswer: 1,
    explanation: "La main gauche représente le potentiel inné, la droite montre l'évolution."
  },
  {
    id: 3,
    question: "Le mont de Vénus est associé à :",
    options: ["L'ambition", "L'amour et la sensualité", "La logique", "Les voyages"],
    correctAnswer: 1,
    explanation: "Le mont de Vénus, à la base du pouce, représente l'amour et les émotions."
  },
  {
    id: 4,
    question: "Une étoile sur la main symbolise :",
    options: ["Malchance", "Un événement intense", "La paix", "L'ennui"],
    correctAnswer: 1,
    explanation: "Une étoile symbolise un événement rapide et intense dans votre vie."
  },
  {
    id: 5,
    question: "La chiromancie est :",
    options: ["Une science exacte", "Un outil de prédiction", "Un outil d'orientation", "De la magie"],
    correctAnswer: 2,
    explanation: "La chiromancie montre des tendances, pas des absolus. C'est un outil d'orientation."
  }
];

const HomePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  
  // Quiz state
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const handleValidate = () => {
    if (selectedAnswer === null) return;
    const isCorrect = selectedAnswer === MINI_QUIZ[currentQuestion].correctAnswer;
    if (isCorrect) setScore(score + 1);
    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentQuestion < MINI_QUIZ.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
    }
  };

  const restartQuiz = () => {
    setQuizStarted(false);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setQuizCompleted(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      {/* Top Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Global Brand - Link back to REVELIAE */}
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
            data-testid="reveliae-home-link"
          >
            <span className="font-heading text-xl font-bold tracking-wide bg-gradient-to-r from-amber-600 to-amber-500 bg-clip-text text-transparent">
              REVELIAE
            </span>
            <span className="text-muted-foreground text-sm">/ LUMINA</span>
          </div>
          
          {/* Auth Buttons */}
          <div className="flex items-center gap-4">
          {!isLoading && (
            <>
              {isAuthenticated && user ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-card/80 backdrop-blur-sm px-4 py-2 rounded-full border border-border">
                    <User className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{user.first_name || user.name}</span>
                  </div>
                  <Button
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="rounded-full"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Déconnexion
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="login-btn"
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  size="sm"
                  className="rounded-full bg-card/80 backdrop-blur-sm"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Connexion
                </Button>
              )}
            </>
          )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="inline-block">
                <span className="uppercase tracking-widest text-xs font-semibold text-muted-foreground">
                  Lumina
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                Découvrez l'histoire
                <span className="block text-primary mt-2">dans vos mains</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-lg">
                Une lecture moderne des lignes de la main propulsée par l'IA.
                Explorez votre avenir avec sagesse et clarté.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="discover-palmistry-btn"
                  onClick={() => navigate('/knowledge')}
                  size="lg"
                  variant="outline"
                  className="rounded-full border-primary/50 text-primary hover:bg-primary/10 px-8 py-6 text-base font-semibold transition-all duration-300"
                >
                  <Book className="mr-2 h-5 w-5" />
                  Découvrir la chiromancie
                </Button>
                <Button
                  data-testid="quiz-main-btn"
                  onClick={() => navigate('/quiz')}
                  size="lg"
                  variant="outline"
                  className="rounded-full border-indigo-400 text-indigo-600 hover:bg-indigo-50 px-8 py-6 text-base font-semibold transition-all duration-300"
                >
                  <Brain className="mr-2 h-5 w-5" />
                  Quiz
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-3">
                <Button
                  data-testid="start-reading-btn"
                  onClick={() => navigate('/scan')}
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Hand className="mr-2 h-5 w-5" />
                  Commencer ma lecture
                </Button>
                <Button
                  data-testid="chat-general-btn"
                  onClick={() => navigate('/chat-general')}
                  size="lg"
                  className="rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Poser une question
                </Button>
              </div>
              
              <div className="flex items-center gap-4 mt-4">
                <Button
                  data-testid="tutorial-btn"
                  onClick={() => navigate('/tutorial')}
                  variant="link"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Comment ça marche ?
                </Button>
                <Button
                  data-testid="view-history-btn"
                  onClick={() => navigate('/history')}
                  variant="link"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <History className="mr-2 h-4 w-4" />
                  Mon historique
                </Button>
              </div>
              
              {/* Quick Access Links */}
              <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-border/50">
                <Button
                  data-testid="quiz-btn"
                  onClick={() => navigate('/quiz')}
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-xs"
                >
                  <Brain className="mr-1.5 h-3.5 w-3.5 text-indigo-500" />
                  Quiz
                </Button>
                <Button
                  data-testid="favorites-btn"
                  onClick={() => navigate('/favorites')}
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-xs"
                >
                  <Star className="mr-1.5 h-3.5 w-3.5 text-yellow-500" />
                  Favoris
                </Button>
                <Button
                  data-testid="reminders-btn"
                  onClick={() => navigate('/reminders')}
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-xs"
                >
                  <Bell className="mr-1.5 h-3.5 w-3.5 text-purple-500" />
                  Rappels
                </Button>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[3/4]">
                <img
                  src="https://images.unsplash.com/photo-1731799843829-70aee8447bd7"
                  alt="Hand against sky"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
              </div>
              <motion.div
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="absolute -top-6 -right-6 bg-secondary p-4 rounded-2xl shadow-xl"
              >
                <Sparkles className="h-8 w-8 text-secondary-foreground" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Features Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Comment ça marche ?</h2>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
              Quatre étapes simples pour découvrir votre destinée
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                icon: LogIn,
                title: 'Créez votre compte',
                description: 'Inscrivez-vous gratuitement en quelques secondes pour accéder à toutes les fonctionnalités',
                highlight: true,
                image: 'https://images.pexels.com/photos/5701329/pexels-photo-5701329.jpeg?auto=compress&cs=tinysrgb&w=400&h=200&fit=crop',
              },
              {
                icon: Hand,
                title: 'Capturez votre main',
                description: 'Prenez une photo ou uploadez une image claire de votre paume',
                image: 'https://images.pexels.com/photos/8058729/pexels-photo-8058729.jpeg?auto=compress&cs=tinysrgb&w=400&h=200&fit=crop',
              },
              {
                icon: Heart,
                title: 'Choisissez vos thèmes',
                description: 'Sélectionnez les aspects de votre vie à explorer',
                image: 'https://images.unsplash.com/photo-1743612266425-40b58038397f?w=400&h=200&fit=crop',
              },
              {
                icon: Sparkles,
                title: 'Recevez votre lecture',
                description: "Découvrez une analyse détaillée propulsée par l'IA",
                image: 'https://images.unsplash.com/photo-1459624470348-67edb45d81b1?w=400&h=200&fit=crop',
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`rounded-2xl border shadow-sm hover:shadow-md transition-all duration-300 flex flex-col overflow-hidden ${
                  feature.highlight 
                    ? 'bg-gradient-to-br from-primary/10 to-primary/5 border-primary/30' 
                    : 'bg-card border-border'
                }`}
              >
                {/* Image */}
                <div className="relative h-32 overflow-hidden">
                  <img 
                    src={feature.image} 
                    alt={feature.title}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    feature.highlight 
                      ? 'bg-gradient-to-t from-primary/40 to-transparent' 
                      : 'bg-gradient-to-t from-black/30 to-transparent'
                  }`} />
                  <div className={`absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    feature.highlight 
                      ? 'bg-primary text-white' 
                      : 'bg-white/90 text-primary shadow-sm'
                  }`}>
                    {index + 1}
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-5 flex flex-col flex-grow">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      feature.highlight 
                        ? 'bg-primary/20' 
                        : 'bg-secondary/50'
                    }`}>
                      <feature.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-base">{feature.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed flex-grow">
                    {feature.description}
                  </p>
                  {feature.highlight && (
                    <div className="mt-4 pt-4 border-t border-primary/20">
                      <Button
                        data-testid="signup-card-btn"
                        onClick={() => navigate('/auth')}
                        className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-5 text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                      >
                        <LogIn className="mr-2 h-4 w-4" />
                        S'inscrire
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Knowledge Link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-8 rounded-3xl shadow-xl max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Book className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-2xl text-white">Base de connaissances</h3>
              </div>
              <p className="text-white/90 mb-6 text-sm md:text-base">
                Plongez dans l'univers fascinant de la chiromancie. Découvrez la signification des lignes, 
                des monts et des observations anatomiques.
              </p>
              <Button
                data-testid="knowledge-link-btn"
                onClick={() => navigate('/knowledge')}
                variant="secondary"
                size="lg"
                className="rounded-full bg-white text-orange-600 hover:bg-white/90 px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Book className="mr-2 h-5 w-5" />
                Découvrir la chiromancie
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quiz Section */}
      <section className="py-16 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Brain className="h-4 w-4" />
              Quiz interactif
            </div>
            <h2 className="font-heading text-3xl md:text-4xl mb-4">Testez vos connaissances</h2>
            <p className="text-muted-foreground text-base md:text-lg">
              5 questions pour découvrir les bases de la chiromancie
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-xl border border-indigo-100 overflow-hidden"
          >
            {!quizStarted ? (
              <div className="p-8 md:p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <Brain className="h-10 w-10 text-white" />
                </div>
                <h3 className="font-heading text-2xl mb-4">Prêt à jouer ?</h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  Découvrez si vous connaissez les bases de la lecture des lignes de la main en 5 questions rapides.
                </p>
                <Button
                  onClick={() => setQuizStarted(true)}
                  size="lg"
                  className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white px-10 py-6 text-base font-semibold shadow-lg"
                >
                  <Sparkles className="mr-2 h-5 w-5" />
                  Commencer le quiz
                </Button>
              </div>
            ) : quizCompleted ? (
              <div className="p-8 md:p-12 text-center">
                <div className="text-5xl mb-4">
                  {score >= 4 ? "🏆" : score >= 3 ? "🌟" : score >= 2 ? "👍" : "📚"}
                </div>
                <h3 className="font-heading text-2xl mb-2">
                  {score >= 4 ? "Excellent !" : score >= 3 ? "Très bien !" : score >= 2 ? "Pas mal !" : "Continuez à apprendre !"}
                </h3>
                <p className="text-4xl font-bold text-indigo-600 mb-4">{score}/{MINI_QUIZ.length}</p>
                <p className="text-muted-foreground mb-8">
                  {score >= 4 
                    ? "Vous êtes prêt pour une lecture de main !" 
                    : "Découvrez notre base de connaissances pour en apprendre plus."}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={restartQuiz}
                    variant="outline"
                    className="rounded-full px-6"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Recommencer
                  </Button>
                  <Button
                    onClick={() => navigate('/quiz')}
                    variant="outline"
                    className="rounded-full px-6 border-indigo-300 text-indigo-600"
                  >
                    <Brain className="mr-2 h-4 w-4" />
                    Quiz complet (20 questions)
                  </Button>
                  <Button
                    onClick={() => navigate('/scan')}
                    className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6"
                  >
                    <Hand className="mr-2 h-4 w-4" />
                    Faire ma lecture
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                {/* Progress bar */}
                <div className="h-2 bg-indigo-100">
                  <motion.div
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-600"
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentQuestion + 1) / MINI_QUIZ.length) * 100}%` }}
                  />
                </div>
                
                <div className="p-6 md:p-8">
                  {/* Question counter */}
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-sm text-muted-foreground">Question {currentQuestion + 1}/{MINI_QUIZ.length}</span>
                    <span className="text-sm font-medium text-indigo-600">Score: {score}</span>
                  </div>

                  {/* Question */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentQuestion}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <h3 className="font-heading text-xl md:text-2xl mb-6">
                        {MINI_QUIZ[currentQuestion].question}
                      </h3>

                      {/* Options */}
                      <div className="space-y-3 mb-6">
                        {MINI_QUIZ[currentQuestion].options.map((option, index) => {
                          const isSelected = selectedAnswer === index;
                          const isCorrect = index === MINI_QUIZ[currentQuestion].correctAnswer;
                          
                          let bgClass = "bg-gray-50 hover:bg-gray-100 border-gray-200";
                          if (showExplanation) {
                            if (isCorrect) bgClass = "bg-green-100 border-green-500";
                            else if (isSelected) bgClass = "bg-red-100 border-red-500";
                          } else if (isSelected) {
                            bgClass = "bg-indigo-100 border-indigo-500";
                          }

                          return (
                            <button
                              key={index}
                              onClick={() => handleAnswerSelect(index)}
                              disabled={showExplanation}
                              className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${bgClass}`}
                            >
                              <span>{option}</span>
                              {showExplanation && isCorrect && <Check className="h-5 w-5 text-green-600" />}
                              {showExplanation && isSelected && !isCorrect && <X className="h-5 w-5 text-red-600" />}
                            </button>
                          );
                        })}
                      </div>

                      {/* Explanation */}
                      <AnimatePresence>
                        {showExplanation && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className={`p-4 rounded-xl mb-6 ${
                              selectedAnswer === MINI_QUIZ[currentQuestion].correctAnswer
                                ? 'bg-green-50 border border-green-200'
                                : 'bg-amber-50 border border-amber-200'
                            }`}
                          >
                            <p className="text-sm">
                              <strong>{selectedAnswer === MINI_QUIZ[currentQuestion].correctAnswer ? '✓ Correct !' : '💡 Explication :'}</strong>{' '}
                              {MINI_QUIZ[currentQuestion].explanation}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Actions */}
                      <div className="flex justify-end">
                        {!showExplanation ? (
                          <Button
                            onClick={handleValidate}
                            disabled={selectedAnswer === null}
                            className="rounded-full bg-indigo-600 hover:bg-indigo-700 px-8"
                          >
                            Valider
                          </Button>
                        ) : (
                          <Button
                            onClick={handleNext}
                            className="rounded-full bg-indigo-600 hover:bg-indigo-700 px-8"
                          >
                            {currentQuestion < MINI_QUIZ.length - 1 ? (
                              <>Suivante <ChevronRight className="ml-2 h-4 w-4" /></>
                            ) : (
                              <>Voir mes résultats <Trophy className="ml-2 h-4 w-4" /></>
                            )}
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            )}
          </motion.div>

          {/* Link to full quiz */}
          {!quizStarted && (
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-6 text-sm text-muted-foreground"
            >
              Envie de plus ? <button onClick={() => navigate('/quiz')} className="text-indigo-600 hover:underline font-medium">Quiz complet (20 questions)</button>
            </motion.p>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="py-20 px-4 md:px-8 lg:px-12"
      >
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-12 md:p-16 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://images.unsplash.com/photo-1717395037173-9d79e4280b69"
              alt="Texture"
              className="w-full h-full object-cover mix-blend-multiply"
            />
          </div>
          <div className="relative z-10">
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Prêt à découvrir votre avenir ?
            </h2>
            <p className="text-white/90 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              Rejoignez des milliers de personnes qui ont déjà exploré leur destinée
            </p>
            <Button
              data-testid="cta-start-reading-btn"
              onClick={() => navigate('/scan')}
              size="lg"
              className="rounded-full bg-white text-primary hover:bg-white/90 px-10 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Commencer maintenant
            </Button>
          </div>
        </div>
      </motion.section>

      {/* Footer Disclaimer */}
      <footer className="bg-amber-50 border-t-2 border-amber-200">
        <div className="max-w-5xl mx-auto px-4 py-8 md:py-12">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            
            <div className="flex-1">
              <h3 className="font-heading text-lg md:text-xl text-amber-900 mb-4 flex items-center gap-2">
                <Info className="h-5 w-5" />
                Information importante
              </h3>
              
              <div className="text-sm text-amber-800 space-y-4">
                <p className="leading-relaxed">
                  <strong>Il n'existe aucune validation scientifique</strong> démontrant que les lignes de la main permettent de dater des événements ou de prédire l'avenir.
                  Les interprétations sont subjectives et varient avec le temps. Les lignes évoluent légèrement avec l'âge, les mouvements, la peau et les habitudes de vie — ce qui rend toute lecture chronologique fragile.
                </p>
                
                <p className="text-amber-700 italic">
                  La pratique de la chiromancie est basée sur une expérience ésotérique et non scientifique.
                </p>
                
                <div className="bg-white/70 rounded-xl p-5 border border-amber-200">
                  <p className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    L'intérêt de l'observation des mains peut servir à :
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-amber-800">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Détecter précocement des signaux faibles
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Encourager un bilan médical
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Engager une prise de conscience sur les habitudes
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      Renforcer la prévention santé
                    </li>
                  </ul>
                </div>
                
                <div className="text-center pt-4 border-t border-amber-200">
                  <p className="text-base font-semibold text-amber-900">
                    Ce n'est pas un outil de prédiction.
                  </p>
                  <p className="text-lg font-bold text-primary mt-1">
                    C'est un outil d'orientation et d'alerte.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t border-amber-200 text-center text-xs text-amber-600">
            <p>Un produit de <strong>C4U Aventures</strong></p>
            <p className="mt-1">© {new Date().getFullYear()} Lumina • Application de lecture de main par IA</p>
          </div>
        </div>
      </footer>

      {/* Quiz, Découvrir, Chat Section */}
      <QuizDiscoverChatSection
        theme="amber"
        appName="lumina"
        quizPath="/quiz"
        quizTitle="Quiz Chiromancie"
        quizDescription="Testez vos connaissances sur les lignes de la main"
        quizFeatures={['20 questions', 'Explications détaillées', 'Score final']}
        discoverPath="/knowledge"
        discoverTitle="Découvrir"
        discoverDescription="Apprenez les bases de la chiromancie"
        discoverFeatures={['Les lignes principales', 'Les monts', 'Les symboles']}
        chatPath="/chat-general"
        chatTitle="Poser une Question"
        chatDescription="Discutez avec Chira, notre experte IA"
        chatExpertName="Chira"
        chatFeatures={['Chat IA', 'Réponses personnalisées', 'Conseils pratiques']}
      />

      {/* Playlist Toggle */}
      <PlaylistToggle theme="amber" app="lumina" />
    </div>
  );
};

export default HomePage;
